/**
 * The shopper's cart token.
 *
 * Unlike the access token, this one *does* belong in localStorage: it is an
 * opaque handle to an anonymous cart, it holds no authority over an account,
 * and a shopper expects their basket to survive closing the tab.
 */
const STORAGE_KEY = 'neon_cart_token';

/**
 * One shop's cart handle.
 *
 * Keyed by the data source, because a browser can hold more than one basket at
 * once: a merchant walking through three theme previews and their own live
 * shop has four, and a single key would have each one emptying the last. The
 * live shop keeps the original key, so no existing shopper loses their basket
 * to this change.
 */
function keyFor(sourceKey: string): string {
  return sourceKey === 'live' ? STORAGE_KEY : `${STORAGE_KEY}:${sourceKey}`;
}

export interface CartTokenStore {
  get(): string | null;
  set(token: string): void;
  clear(): void;
}

export function cartTokenFor(sourceKey: string): CartTokenStore {
  const key = keyFor(sourceKey);
  return {
    get() {
      try {
        return localStorage.getItem(key);
      } catch {
        return null; // private browsing, storage disabled
      }
    },

    set(token: string) {
      try {
        localStorage.setItem(key, token);
      } catch {
        /* the cart simply will not persist */
      }
    },

    clear() {
      try {
        localStorage.removeItem(key);
      } catch {
        /* nothing to do */
      }
    },
  };
}

/** The merchant's own shop, for callers that are only ever in it. */
export const cartToken = cartTokenFor('live');
